import { useMemo, useState } from 'react';
import { Clapperboard, Shuffle, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  editorAnimationSpeedFromSlider,
  editorAnimationSpeedLabel,
  editorAnimationSpeedToSlider,
  MOTION_SPEED_SLIDER_MAX,
  MOTION_SPEED_SLIDER_MIN,
} from '@/lib/editorAnimationSettings';
import {
  songAnimationSpeedFromSlider,
  songAnimationSpeedToSlider,
  songBeatSyncSpeedLabel,
} from '@/lib/songAnimationSettings';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  motionPresetLabel,
  motionPresetsByCategory,
  MOTION_PRESETS,
  normalizeMotionPreset,
  STORY_VIDEO_MOTION_PRESET_IDS,
} from '@/lib/videoMotionPresets';
import { TimelineLaneMinimizeButton } from '@/components/videoEditor/TimelineLaneMinimizeButton';
import { timelineLaneClipAreaStyle, timelineLaneRowSizeStyle } from '@/lib/videoEditorTimelineLanes';
import { clipsInTimelineViewport } from '@/lib/timelineViewportClips';

const CATEGORY_COLORS = {
  zoom: '#8b5cf6',
  pan: '#3b82f6',
  dolly: '#6366f1',
  tracking: '#0ea5e9',
  crane: '#14b8a6',
  rotation: '#f59e0b',
  tilt: '#10b981',
  combined: '#ec4899',
  cinematic: '#a855f7',
  drone: '#64748b',
};

function presetColor(presetId) {
  const p = MOTION_PRESETS.find((row) => row.id === normalizeMotionPreset(presetId));
  return CATEGORY_COLORS[p?.category] || '#8b5cf6';
}

const TIMELINE_LABEL_STICKY_CLASS =
  'w-36 max-w-[9rem] shrink-0 sticky left-0 border-r shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)] overflow-hidden';
const TIMELINE_LABEL_PANEL_STYLE = {
  background: 'var(--bg-secondary)',
  borderColor: 'var(--glass-border)',
  zIndex: 100,
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Animation lane — one bar per image clip; edits motionPreset on linked video clip.
 */
export function AnimationTimelineTrack({
  clips = [],
  pxPerSec,
  timelineActive,
  selectedClipId,
  onToggleActive,
  onSelectClip,
  onPresetChange,
  onApplyToAll,
  onApplyRandomStory,
  onApplyRandomAll,
  onBeginResize,
  editorAnimationSpeed = 1,
  onEditorAnimationSpeedChange,
  isSongBased = false,
  songBeatSyncSpeed = 0.38,
  onSongBeatSyncSpeedChange,
  songBeatSyncEnabled = true,
  collapsed = false,
  onToggleCollapsed,
  viewportStartSec = 0,
  viewportEndSec = Number.POSITIVE_INFINITY,
}) {
  const grouped = useMemo(() => motionPresetsByCategory(), []);
  const [bulkPreset, setBulkPreset] = useState('ken_burns');
  const visibleClips = useMemo(
    () => clipsInTimelineViewport(clips, {
      startSec: viewportStartSec,
      endSec: viewportEndSec,
      forceIds: selectedClipId ? new Set([selectedClipId]) : null,
    }),
    [clips, viewportStartSec, viewportEndSec, selectedClipId],
  );

  return (
    <div
      className="flex border-b"
      style={{
        borderColor: 'var(--glass-border)',
        opacity: timelineActive ? 1 : 0.45,
        ...timelineLaneRowSizeStyle(collapsed, isSongBased ? 148 : 112),
      }}
      data-testid="animation-timeline-track"
    >
      <div
        className={`${TIMELINE_LABEL_STICKY_CLASS} flex flex-col justify-center gap-1 px-2 ${collapsed ? 'py-0' : 'py-1.5'}`}
        style={TIMELINE_LABEL_PANEL_STYLE}
      >
        <div className="flex items-center justify-between gap-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            {onToggleCollapsed ? (
              <TimelineLaneMinimizeButton
                collapsed={collapsed}
                onToggle={onToggleCollapsed}
                label="Animations track"
                testId="timeline-lane-minimize-animation"
              />
            ) : null}
            <Clapperboard className="w-3 h-3 shrink-0" style={{ color: '#8b5cf6' }} />
            <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
              Animations
            </span>
          </div>
          <button
            type="button"
            onClick={onToggleActive}
            className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
            style={{
              borderColor: 'var(--glass-border)',
              color: timelineActive ? '#8b5cf6' : 'var(--text-tertiary)',
            }}
            data-testid="animation-lane-active-toggle"
          >
            {timelineActive ? 'Active' : 'Off'}
          </button>
        </div>
        {collapsed ? null : (
        <>
        <Select value={bulkPreset} onValueChange={setBulkPreset}>
          <SelectTrigger className="h-7 w-full min-w-0 text-[10px] px-2" data-testid="animation-bulk-preset-select">
            <SelectValue placeholder="Preset" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {grouped.map((group) => (
              <SelectGroup key={group.id}>
                <SelectLabel className="text-[10px]">{group.label}</SelectLabel>
                {group.presets.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <div className={timelineActive ? '' : 'opacity-45 pointer-events-none'}>
          <div className="flex items-center justify-between text-[9px] mb-0.5"
            style={{ color: 'var(--text-secondary)' }}>
            <span>Animation speed</span>
            <span>{editorAnimationSpeedLabel(editorAnimationSpeed)}</span>
          </div>
          <Slider
            value={[editorAnimationSpeedToSlider(editorAnimationSpeed)]}
            min={MOTION_SPEED_SLIDER_MIN}
            max={MOTION_SPEED_SLIDER_MAX}
            step={1}
            onValueChange={([v]) => onEditorAnimationSpeedChange?.(editorAnimationSpeedFromSlider(v))}
            data-testid="editor-animation-speed-slider"
          />
        </div>
        {isSongBased ? (
          <div className={timelineActive && songBeatSyncEnabled ? '' : 'opacity-45 pointer-events-none'}>
            <div className="flex items-center justify-between text-[9px] mb-0.5"
              style={{ color: 'var(--text-secondary)' }}>
              <span>Beat-sync speed</span>
              <span>{songBeatSyncSpeedLabel(songBeatSyncSpeed)}</span>
            </div>
            <Slider
              value={[songAnimationSpeedToSlider(songBeatSyncSpeed)]}
              min={MOTION_SPEED_SLIDER_MIN}
              max={MOTION_SPEED_SLIDER_MAX}
              step={1}
              className="[&>span:first-child]:bg-gradient-to-r [&>span:first-child]:from-violet-950/60 [&>span:first-child]:to-violet-400/50"
              onValueChange={([v]) => onSongBeatSyncSpeedChange?.(songAnimationSpeedFromSlider(v))}
              data-testid="song-beat-sync-speed-slider"
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1 min-w-0 w-full">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 w-full min-w-0 text-[10px] px-1.5 justify-start truncate"
            onClick={() => onApplyToAll?.(bulkPreset)}
            data-testid="animation-apply-all-btn"
          >
            <Wand2 className="w-3 h-3 mr-1 shrink-0" />
            <span className="truncate">Apply to all images</span>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 w-full min-w-0 text-[10px] px-1.5 justify-start truncate"
            onClick={onApplyRandomStory}
            data-testid="animation-random-story-btn"
          >
            <Shuffle className="w-3 h-3 mr-1 shrink-0" />
            <span className="truncate">Random (story set)</span>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 w-full min-w-0 text-[10px] px-1.5 justify-start truncate"
            onClick={onApplyRandomAll}
            data-testid="animation-random-all-btn"
          >
            <Shuffle className="w-3 h-3 mr-1 shrink-0" />
            <span className="truncate">Random (all)</span>
          </Button>
        </div>
        </>
        )}
      </div>

      <div
        className="relative flex-1 min-w-0 z-0"
        style={{ background: 'var(--bg-primary)', ...timelineLaneClipAreaStyle(collapsed) }}
      >
        {visibleClips.map((clip) => {
          const preset = normalizeMotionPreset(clip.motionPreset);
          const color = presetColor(preset);
          const isSelected = selectedClipId === clip.id;
          const dur = Math.max(0.05, Number(clip.duration) || 0.05);
          const label = preset === 'none' ? 'No motion' : motionPresetLabel(preset);
          return (
            <div
              key={clip.id}
              className="absolute top-1 bottom-1 rounded-lg flex flex-col justify-center px-2 select-none cursor-pointer group overflow-hidden"
              style={{
                left: clip.startTime * pxPerSec,
                width: Math.max(48, dur * pxPerSec - 2),
                background: `${color}28`,
                border: `2px solid ${color}`,
                boxShadow: isSelected ? `0 0 0 2px white, 0 0 0 4px ${color}` : 'none',
                zIndex: isSelected ? 28 : 5,
              }}
              onClick={() => onSelectClip?.(clip)}
              data-testid={`animation-clip-${clip.id}`}
              title={`${clip.name || 'Image'} · ${label}`}
            >
              <span className="text-[9px] font-bold uppercase tracking-wide truncate" style={{ color }}>
                {label}
              </span>
              <span className="text-[9px] truncate opacity-80" style={{ color: 'var(--text-secondary)' }}>
                {clip.name || `Scene ${clip.segmentNumber || ''}`}
              </span>
              {onBeginResize ? (
                <>
                  <div
                    className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                    onMouseDown={(e) => { e.stopPropagation(); onBeginResize(e, clip, 'left'); }}
                    title="Resize animation start"
                  />
                  <div
                    className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/30 transition-opacity"
                    onMouseDown={(e) => { e.stopPropagation(); onBeginResize(e, clip, 'right'); }}
                    title="Resize animation duration"
                  />
                </>
              ) : null}
              <div
                className="opacity-0 group-hover:opacity-100 transition-opacity mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Select
                  value={preset}
                  onValueChange={(next) => onPresetChange?.(clip.id, next)}
                >
                  <SelectTrigger className="h-6 text-[9px] px-1.5 bg-black/20 border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none" className="text-xs">None</SelectItem>
                    {grouped.map((group) => (
                      <SelectGroup key={group.id}>
                        <SelectLabel className="text-[10px]">{group.label}</SelectLabel>
                        {group.presets.map((p) => (
                          <SelectItem key={p.id} value={p.id} className="text-xs">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
        {!clips.length && !collapsed && (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] px-3 text-center"
            style={{ color: 'var(--text-tertiary)' }}>
            Add images on Video 1 — then pick zoom, pan, Ken Burns, and story-video moves here
          </div>
        )}
        {clips.length > 0 && !collapsed && (
          <p className="absolute bottom-0 right-2 text-[9px] pointer-events-none opacity-60"
            style={{ color: 'var(--text-tertiary)' }}>
            Story presets: {STORY_VIDEO_MOTION_PRESET_IDS.length} moves · {clips.length} image{clips.length === 1 ? '' : 's'}
          </p>
        )}
      </div>
    </div>
  );
}
